import { useState, useEffect } from "react"
import type { EmailAccountFilters } from "~/react/components/email_accounts/email_accounts_filters"

const getFiltersFromLocation = (): EmailAccountFilters => {
  if (typeof window === "undefined") {
    return {
      status: [],
      healthFrom: null,
      healthTo: null,
      healthIsEmpty: false,
      healthHasAnyValue: false,
      ownerIds: [],
      searchQuery: "",
    }
  }

  const params = new URLSearchParams(window.location.search)

  // Parse status array
  const statusParam = params.get("account_status") || params.get("status")
  const status = statusParam ? statusParam.split("|") : []

  // Parse health range and modes
  const healthHasAnyValue =
    (params.get("account_health_has_any_value") || params.get("health_has_any_value")) === "true"
  const healthFromParam = params.get("account_health_from") || params.get("health_from")
  const parsedHealthFrom = healthFromParam ? parseInt(healthFromParam, 10) : null
  const healthFrom = parsedHealthFrom !== null && Number.isNaN(parsedHealthFrom) ? null : parsedHealthFrom
  const healthToParam = params.get("account_health_to") || params.get("health_to")
  const parsedHealthTo = healthToParam ? parseInt(healthToParam, 10) : null
  const healthTo = parsedHealthTo !== null && Number.isNaN(parsedHealthTo) ? null : parsedHealthTo
  const healthIsEmpty = (params.get("account_health_empty") || params.get("health_empty")) === "true"

  // When "has any value" is set, clear range/empty so only one health mode is active
  const effectiveHealthHasAnyValue = healthHasAnyValue
  const effectiveHealthFrom = healthHasAnyValue ? null : healthFrom
  const effectiveHealthTo = healthHasAnyValue ? null : healthTo
  const effectiveHealthIsEmpty = healthHasAnyValue ? false : healthIsEmpty

  // Parse owner IDs array
  const ownerIdsParam = params.get("account_owner_ids") || params.get("owner_ids")
  const ownerIds = ownerIdsParam
    ? ownerIdsParam
        .split("|")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))
    : []

  // Parse search query
  const searchQuery = params.get("account_query") || params.get("query") || ""

  return {
    status,
    healthFrom: effectiveHealthFrom,
    healthTo: effectiveHealthTo,
    healthIsEmpty: effectiveHealthIsEmpty,
    healthHasAnyValue: effectiveHealthHasAnyValue,
    ownerIds,
    searchQuery,
  }
}

const updateFiltersInURL = (filters: EmailAccountFilters) => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)

  // Update status
  if (filters.status.length > 0) {
    params.set("account_status", filters.status.join("|"))
  } else {
    params.delete("account_status")
    params.delete("status")
  }

  // Update health range
  if (filters.healthFrom !== null) {
    params.set("account_health_from", filters.healthFrom.toString())
  } else {
    params.delete("account_health_from")
    params.delete("health_from")
  }

  if (filters.healthTo !== null) {
    params.set("account_health_to", filters.healthTo.toString())
  } else {
    params.delete("account_health_to")
    params.delete("health_to")
  }

  if (filters.healthIsEmpty) {
    params.set("account_health_empty", "true")
  } else {
    params.delete("account_health_empty")
    params.delete("health_empty")
  }

  // Update health "has any value" (mutually exclusive with range/empty)
  if (filters.healthHasAnyValue) {
    params.set("account_health_has_any_value", "true")
    params.delete("account_health_from")
    params.delete("health_from")
    params.delete("account_health_to")
    params.delete("health_to")
    params.delete("account_health_empty")
    params.delete("health_empty")
  } else {
    params.delete("account_health_has_any_value")
    params.delete("health_has_any_value")
  }

  // Update owner IDs
  if (filters.ownerIds.length > 0) {
    params.set("account_owner_ids", filters.ownerIds.join("|"))
  } else {
    params.delete("account_owner_ids")
    params.delete("owner_ids")
  }

  // Update search query
  if (filters.searchQuery) {
    params.set("account_query", filters.searchQuery)
  } else {
    params.delete("account_query")
    params.delete("query")
  }

  // Update URL without reload
  const queryString = params.toString()
  const newURL = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  window.history.replaceState(null, "", newURL)
}

export const DEFAULT_FILTERS: EmailAccountFilters = {
  status: [],
  healthFrom: null,
  healthTo: null,
  healthIsEmpty: false,
  healthHasAnyValue: false,
  ownerIds: [],
  searchQuery: "",
}

export function useURLFilters() {
  const [filters, setFilters] = useState<EmailAccountFilters>(() => getFiltersFromLocation())

  // Sync filters to URL whenever they change
  useEffect(() => {
    updateFiltersInURL(filters)
  }, [filters])

  return [filters, setFilters] as const
}
